import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ExternalLink, Gauge, Users, ChevronRight, Star, Route, Video,
} from "lucide-react";
import { getVideoEmbedUrl, getVideoExternalUrl, PlatformIcon } from "@/components/YoutubeVideoManager";
import { getTranslatedValue } from "@/components/TranslatableField";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { getUploadUrl, getCloudinaryThumbnail, quoteNickname } from "@/lib/utils";
import { ParticipantLink } from "./ParticipantLink";
import { BACKEND_URL } from "../../config";

export function TripSidebar({
  trip, allPhotos, currentLang, t, isAuthenticated, navigate, tripId,
  isOwner,
}) {
  return (
    <div className="space-y-6">
      {/* b99.9.99 (2026-05-19) — Photos+PhotosMap+GalleryLink card removed
          from the sidebar at user request. The main photo is now rendered
          standalone in TripDetail's right column (mainPhotoCard); the
          gallery link is surfaced as a hover overlay there. The dedicated
          PhotosMap (lighthouse + Leaflet preview) was deemed redundant
          with the full TripOverviewMap in the left column. */}

      {/* Event Card */}
      {(trip.event_url || getTranslatedValue(trip.event_title, currentLang) || trip.event_photo) && (
        <Card className="bg-card border-border rounded-xl overflow-hidden">
          <div className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <ExternalLink className="h-4 w-4" />
              <span>{t("trip_detail.event")}</span>
            </div>
            {trip.event_url ? (
              <a
                href={trip.event_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
                data-testid="trip-event-card-link"
                onClick={(e) => {
                  if (!isAuthenticated) {
                    e.preventDefault();
                    handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.event") });
                    window.scrollTo(0, 0);
                  }
                }}
              >
                {trip.event_photo && (
                  <div className="relative overflow-hidden rounded-lg bg-muted/20 flex items-center justify-center mb-3">
                    <img src={getCloudinaryThumbnail(getUploadUrl(trip.event_photo), 500, 300, "fit")} alt={getTranslatedValue(trip.event_title, currentLang) || t("trip_detail.event")} className="max-w-full max-h-40 object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <ExternalLink className="h-6 w-6 text-white" />
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="font-medium text-primary group-hover:underline">{getTranslatedValue(trip.event_title, currentLang) || t("trip_detail.view_event")}</span>
                  <ExternalLink className="h-4 w-4 text-primary" />
                </div>
              </a>
            ) : (
              <div data-testid="trip-event-card-static">
                {trip.event_photo && (
                  <div className="overflow-hidden rounded-lg bg-muted/20 flex items-center justify-center mb-3">
                    <img src={getCloudinaryThumbnail(getUploadUrl(trip.event_photo), 500, 300, "fit")} alt={getTranslatedValue(trip.event_title, currentLang) || t("trip_detail.event")} className="max-w-full max-h-40 object-contain" loading="lazy" />
                  </div>
                )}
                <span className="font-medium text-foreground">{getTranslatedValue(trip.event_title, currentLang)}</span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Club Card */}
      {trip.club && (trip.group?.club_id || trip.club?.club_id || trip.club_id) && (
        <Card
          className={`bg-card border-border rounded-xl overflow-hidden ${trip.club.can_access ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""}`}
          onClick={() => { if (trip.club.can_access) { navigate(`/clube/${trip.club.club_id}`); window.scrollTo(0, 0); } }}
          data-testid="trip-club-card"
        >
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-primary" />
              {t("trip_detail.club")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {(trip.club.logo || trip.club.photo) ? (
                <div className="aspect-square w-16 flex-shrink-0">
                  <img
                    loading="lazy"
                    src={getCloudinaryThumbnail(getUploadUrl(trip.club.logo || trip.club.photo), 128, 128)}
                    alt={getTranslatedValue(trip.club.name, currentLang)}
                    className="w-full h-full rounded-lg object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-square w-16 flex-shrink-0 rounded-lg bg-muted flex items-center justify-center">
                  <Users className="h-6 w-6 text-muted-foreground/50" />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-bold text-lg text-primary">{getTranslatedValue(trip.club.name, currentLang)}</h3>
                {trip.club.description && (
                  <p className="text-sm text-muted-foreground line-clamp-4">{getTranslatedValue(trip.club.description, currentLang)}</p>
                )}
              </div>
              {trip.club.can_access && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Motorcycle Card — only for personal trips */}
      {trip.motorcycle && trip.owner_type !== "club" && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Card
                className="bg-card border-border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => { if (trip.motorcycle.motorcycle_id) navigate(`/mota/${trip.motorcycle.motorcycle_id}`); }}
                data-testid="motorcycle-card-link"
              >
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Gauge className="h-5 w-5 text-primary" />
                    {t("trip_detail.motorcycle")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    {trip.motorcycle.photos?.length > 0 ? (() => {
                      const selectedPhoto = trip.motorcycle_photo_id ? trip.motorcycle.photos.find((p) => p.photo_id === trip.motorcycle_photo_id) : null;
                      const displayPhoto = selectedPhoto || trip.motorcycle.photos.find((p) => p.is_main) || trip.motorcycle.photos[0];
                      const photoUrl = displayPhoto?.url?.startsWith("http") ? displayPhoto.url : `${BACKEND_URL}${displayPhoto?.url}`;
                      return displayPhoto ? (
                        <div className="aspect-square w-16 flex-shrink-0">
                          <img loading="lazy" src={photoUrl} alt={getTranslatedValue(trip.motorcycle.nickname, currentLang)} className="w-full h-full rounded-lg object-cover"
                            onError={(e) => { e.target.parentElement.innerHTML = `<div class="w-full h-full rounded-lg bg-muted flex items-center justify-center"><span class="text-xl font-black text-muted-foreground/50">${getTranslatedValue(trip.motorcycle.nickname, currentLang)?.charAt(0)?.toUpperCase() || "M"}</span></div>`; }}
                          />
                        </div>
                      ) : null;
                    })() : (
                      <div className="aspect-square w-16 flex-shrink-0 rounded-lg bg-muted flex items-center justify-center">
                        <span className="text-2xl font-black text-muted-foreground/50">{getTranslatedValue(trip.motorcycle.nickname, currentLang)?.charAt(0)?.toUpperCase() || "M"}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-primary">{quoteNickname(getTranslatedValue(trip.motorcycle.nickname, currentLang))}</h3>
                      {(trip.motorcycle.brand || trip.motorcycle.model) && (
                        <p className="text-sm text-muted-foreground">{[trip.motorcycle.brand, trip.motorcycle.model].filter(Boolean).join(" ")}{trip.motorcycle.year && ` (${trip.motorcycle.year})`}</p>
                      )}
                      {trip.motorcycle.displacement && <p className="text-xs text-muted-foreground">{trip.motorcycle.displacement} cc</p>}
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent><p>{t("trip_detail.view_motorcycle")}</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Videos Cards */}
      {trip.youtube_videos?.length > 0 && (
        <Card className="bg-card border-border rounded-xl">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Video className="h-5 w-5 text-primary" />
              {t("trip_detail.videos")} ({trip.youtube_videos.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {trip.youtube_videos.sort((a, b) => (a.order || 0) - (b.order || 0)).map((video) => (
              <div key={video.video_id} className="rounded-lg overflow-hidden border border-border">
                <div className="aspect-video">
                  <iframe src={getVideoEmbedUrl(video)} title={video.title || "Video"} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
                </div>
                {video.title && (
                  <div className="p-2 bg-muted/50 flex items-center gap-2">
                    <PlatformIcon platform={video.platform} className="h-3.5 w-3.5 flex-shrink-0" />
                    <p className="font-medium text-sm">{video.title}</p>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Group Card */}
      {trip.group && (
        <Card className="bg-card border-border rounded-xl">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-primary" />
              {t("trip_detail.group")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`flex items-center gap-4 rounded-lg p-2 -m-2 transition-colors ${trip.club.can_access ? "cursor-pointer hover:bg-muted/50" : "opacity-80"}`}
              onClick={() => {
                if (!isAuthenticated) { handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.group") }); window.scrollTo(0, 0); return; }
                if (trip.club.can_access) { navigate(`/clube/${trip.club.club_id}`); window.scrollTo(0, 0); }
              }}
            >
              {(trip.club.logo || trip.club.photo) ? (
                <div className="aspect-square w-16 flex-shrink-0">
                  <img src={getCloudinaryThumbnail(getUploadUrl(trip.club.logo || trip.club.photo), 128, 128)} alt={getTranslatedValue(trip.club.name, currentLang)} className="w-full h-full rounded-lg object-cover" loading="lazy" />
                </div>
              ) : (
                <div className="aspect-square w-16 flex-shrink-0 rounded-lg bg-primary/20 flex items-center justify-center"><Users className="h-6 w-6 text-primary" /></div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg truncate">{getTranslatedValue(trip.club.name, currentLang)}</h3>
                {trip.club.description && <p className="text-sm text-muted-foreground line-clamp-4">{getTranslatedValue(trip.club.description, currentLang)}</p>}
                {!trip.club.can_access && <p className="text-xs text-muted-foreground mt-1">{t("trip_detail.private_group")}</p>}
              </div>
              {trip.club.can_access && <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invited Clubs Card */}
      {trip.associated_clubs?.length > 0 && (
        <Card className="bg-card border-border rounded-xl" data-testid="associated-clubs-card">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-primary" />
              {t("trip_detail.invited_clubs")} ({trip.associated_clubs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {trip.associated_clubs.map((club) => (
                <div
                  key={club.club_id}
                  className={`flex items-center gap-4 p-2 -mx-2 rounded-lg transition-colors ${club.can_access ? "cursor-pointer hover:bg-muted/50" : "opacity-80"}`}
                  onClick={() => {
                    if (!isAuthenticated) { handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.group") }); window.scrollTo(0, 0); return; }
                    if (club.can_access) { navigate(`/clube/${club.club_id}`); window.scrollTo(0, 0); }
                  }}
                  data-testid={`associated-club-${club.club_id}`}
                >
                  {(club.logo || club.photo) ? (
                    <div className="aspect-square w-16 flex-shrink-0">
                      <img src={getCloudinaryThumbnail(getUploadUrl(club.logo || club.photo), 128, 128)} alt={getTranslatedValue(club.name, currentLang)} className="w-full h-full rounded-lg object-cover" loading="lazy" />
                    </div>
                  ) : (
                    <div className="aspect-square w-16 flex-shrink-0 rounded-lg bg-primary/20 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-lg text-primary truncate">{getTranslatedValue(club.name, currentLang)}</h4>
                    {!club.can_access && <p className="text-xs text-muted-foreground">{t("trip_detail.private_group")}</p>}
                  </div>
                  {club.can_access && <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participants Card */}
      {trip.participants?.length > 0 && (
        <Card className="bg-card border-border rounded-xl">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-primary" />
              {t("trip_detail.participants")} ({trip.participants.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TooltipProvider>
              <div className="grid grid-cols-1 gap-2">
                {[...trip.participants].sort((a, b) => {
                  const roleOrder = { owner: 0, moderator: 1, member: 2 };
                  const ra = roleOrder[a.club_role] ?? 3;
                  const rb = roleOrder[b.club_role] ?? 3;
                  if (ra !== rb) return ra - rb;
                  return (a.nickname || a.name || "").localeCompare(b.nickname || b.name || "", "pt");
                }).map((participant, idx) => (
                  <ParticipantLink key={`${participant.participant_id}-${idx}`} participant={participant} isAuthenticated={isAuthenticated} />
                ))}
              </div>
            </TooltipProvider>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
